// client/src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { adminAPI } from '../../services/api';
import { format } from 'date-fns';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [approvalStats, setApprovalStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchDashboard();
  }, [user, navigate]);

  const fetchDashboard = async () => {
    try {
      const [dashboardRes, approvalsRes] = await Promise.all([
        adminAPI.getDashboard(),
        adminAPI.getApprovalStats().catch(() => ({ data: null }))
      ]);
      
      setDashboard(dashboardRes.data.data || dashboardRes.data);
      setApprovalStats(approvalsRes.data);
    } catch (err) {
      setError('Failed to load dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">{error}</div>
          <button
            onClick={fetchDashboard}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const pendingCount = approvalStats?.total?.pending || 0;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-indigo-900 shadow-lg">
        <div className="container mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center">
                🔐 Admin Dashboard
              </h1>
              <p className="text-blue-200 mt-1">WholesaleHub Management Console</p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Pending Approvals Badge */}
              {pendingCount > 0 && (
                <button
                  onClick={() => navigate('/admin/approvals')}
                  className="relative bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors flex items-center"
                >
                  <span className="mr-2">⏳</span>
                  {pendingCount} Pending
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
                    !
                  </span>
                </button>
              )}
              <button
                onClick={() => navigate('/admin/settings')}
                className="text-white hover:text-blue-200 transition-colors"
                title="Admin Settings"
              >
                ⚙️
              </button>
              <div className="text-right">
                <p className="text-sm text-blue-200">Logged in as</p>
                <p className="text-white font-semibold">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {/* Pending Approvals Alert Banner */}
        {pendingCount > 0 && (
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-2xl mr-3">⏳</span>
                <div>
                  <h3 className="font-semibold text-yellow-800">
                    {pendingCount} Registration{pendingCount !== 1 ? 's' : ''} Awaiting Approval
                  </h3>
                  <p className="text-yellow-700 text-sm">
                    New users are waiting for account approval to access the platform.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/admin/approvals')}
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors font-medium"
              >
                Review Now →
              </button>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon="👥"
            color="blue"
            subtitle="All registered users"
          />
          <StatCard
            title="Businesses"
            value={stats.totalBusinesses}
            icon="🏪"
            color="green"
            subtitle="Active businesses"
          />
          <StatCard
            title="Wholesalers"
            value={stats.totalWholesalers}
            icon="🏭"
            color="yellow"
            subtitle="Active suppliers"
          />
          <StatCard
            title="Products"
            value={stats.totalProducts}
            icon="📦"
            color="yellow"
            subtitle="Total catalog"
          />
          <StatCard
            title="Orders"
            value={stats.totalOrders}
            icon="📋"
            color="indigo"
            subtitle="All time orders"
          />
          <StatCard
            title="Revenue"
            value={`€${stats.totalRevenue?.toFixed(2) || '0.00'}`}
            icon="💰"
            color="green"
            subtitle="Total revenue"
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-6">
            {/* Pending Approvals Action (first position) */}
            <ActionButton
              title="Pending Approvals"
              description="Review new registrations"
              icon="✅"
              color={pendingCount > 0 ? 'yellow' : 'gray'}
              onClick={() => navigate('/admin/approvals')}
              badge={pendingCount > 0 ? pendingCount : null}
            />
            <ActionButton
              title="Manage Users"
              description="View and manage all users"
              icon="👥"
              color="blue"
              onClick={() => navigate('/admin/users/all')}
            />
            <ActionButton
              title="Business Users"
              description="Detailed business management"
              icon="🏢"
              color="purple"
              onClick={() => navigate('/admin/users/businesses')}
            />
            <ActionButton
              title="Wholesaler Users"
              description="Manage wholesaler accounts"
              icon="🏭"
              color="indigo"
              onClick={() => navigate('/admin/wholesalers')}
            />
            <ActionButton
              title="View Orders"
              description="Monitor all platform orders"
              icon="📋"
              color="green"
              onClick={() => navigate('/admin/orders')}
            />
            <ActionButton
              title="Billing Management"
              description="Track payments & invoices"
              icon="💳"
              color="yellow"
              onClick={() => navigate('/admin/billing')}
            />
            <ActionButton
              title="Products"
              description="Browse & moderate all products"
              icon="📦"
              color="amber"
              onClick={() => navigate('/admin/products')}
            />
            <ActionButton
              title="Flash Offers"
              description="Monitor flash sales"
              icon="⚡"
              color="red"
              onClick={() => navigate('/admin/flash-offers')}
            />
            <ActionButton
              title="Discounts"
              description="Customer discount relationships"
              icon="🏷️"
              color="green"
              onClick={() => navigate('/admin/discounts')}
            />
            <ActionButton
              title="Analytics"
              description="Platform performance"
              icon="📊"
              color="blue"
              onClick={() => navigate('/admin/analytics')}
            />
            <ActionButton
              title="Announcements"
              description="Broadcast to users"
              icon="📢"
              color="purple"
              onClick={() => navigate('/admin/announcements')}
            />

            <ActionButton
              title="Catalog Discounts"
              description="Catalog-wide discount rules"
              icon="🏷️"
              color="teal"
              onClick={() => navigate('/admin/catalog-discounts')}
            />

            <ActionButton
              title="Concierge Onboarding"
              description="Onboard businesses on behalf of wholesalers"
              icon="🤝"
              color="purple"
              onClick={() => navigate('/admin/onboarding')}
            />

            <ActionButton
              title="System Tools"
              description="Cleanup and maintenance"
              icon="⚙️"
              color="red"
              onClick={() => navigate('/admin/tools')}
            />
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Users */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Recent Users</h2>
              <button
                onClick={() => navigate('/admin/users')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View All →
              </button>
            </div>
            <div className="space-y-3">
              {dashboard?.recentUsers?.map(u => (
                <div key={u._id} className="flex justify-between items-center border-b pb-3 hover:bg-gray-50 p-2 rounded transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                      u.role === 'business' ? 'bg-blue-500' : 
                      u.role === 'wholesaler' ? 'bg-purple-500' : 'bg-gray-500'
                    }`}>
                      {u.email.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">{u.email}</p>
                      <p className="text-sm text-gray-500 capitalize">{u.role}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs rounded font-medium ${
                      u.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">
                      {format(new Date(u.createdAt), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Orders */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Recent Orders</h2>
              <button
                onClick={() => navigate('/admin/orders')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View All →
              </button>
            </div>
            <div className="space-y-3">
              {dashboard?.recentOrders?.slice(0, 5).map(order => (
                <div key={order._id} className="flex justify-between items-center border-b pb-3 hover:bg-gray-50 p-2 rounded transition-colors">
                  <div>
                    <p className="font-medium">{order.orderNumber}</p>
                    <p className="text-sm text-gray-600">{order.businessId?.businessName || 'Unknown'}</p>
                    <p className="text-xs text-gray-400">
                      {format(new Date(order.createdAt), 'MMM d, h:mm a')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">€{order.totals?.total?.toFixed(2)}</p>
                    <span className={`text-xs px-2 py-1 rounded capitalize ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Order Status Breakdown */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Order Status Distribution</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {dashboard?.ordersByStatus?.map(item => (
              <div key={item._id} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-800">{item.count}</p>
                <p className="text-sm text-gray-600 capitalize mt-1">{item._id}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ title, value, icon, color, subtitle }) => {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    yellow: 'from-yellow-500 to-yellow-600',
    indigo: 'from-indigo-500 to-indigo-600',
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
      <div className={`bg-gradient-to-r ${colors[color] || colors.blue} p-4`}>
        <div className="text-white text-3xl mb-2">{icon}</div>
        <div className="text-white">
          <p className="text-sm opacity-90">{title}</p>
          <p className="text-2xl font-bold mt-1">{value || 0}</p>
        </div>
      </div>
      <div className="p-3 bg-gray-50">
        <p className="text-xs text-gray-600">{subtitle}</p>
      </div>
    </div>
  );
};

const ActionButton = ({ title, description, icon, color, onClick, badge }) => {
  const colors = {
    blue: 'bg-blue-50 hover:bg-blue-100 text-blue-600',
    green: 'bg-green-50 hover:bg-green-100 text-green-600',
    yellow: 'bg-yellow-50 hover:bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-50 hover:bg-purple-100 text-purple-600',
    indigo: 'bg-indigo-50 hover:bg-indigo-100 text-indigo-600',
    red: 'bg-red-50 hover:bg-red-100 text-red-600',
    amber: 'bg-amber-50 hover:bg-amber-100 text-amber-600',
    gray: 'bg-gray-50 hover:bg-gray-100 text-gray-600',
  };

  return (
    <button
      onClick={onClick}
      className={`${colors[color]} rounded-lg shadow-md p-6 hover:shadow-lg transition-all text-left w-full relative`}
    >
      {badge && (
        <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
          {badge}
        </span>
      )}
      <div className="text-4xl mb-3">{icon}</div>
      <h3 className="font-bold text-gray-900 text-lg mb-1">{title}</h3>
      <p className="text-sm opacity-75">{description}</p>
    </button>
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case 'delivered':
      return 'bg-green-100 text-green-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    case 'shipped':
      return 'bg-purple-100 text-purple-800';
    case 'processing':
      return 'bg-blue-100 text-blue-800';
    case 'confirmed':
      return 'bg-indigo-100 text-indigo-800';
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

export default AdminDashboard;